import ERC20_ABI from 'abis/erc20.json'
import { KODIAK_FARM_ABI } from 'components/KodiakFarms/KodiakFarm'
import { FarmConfig } from 'components/KodiakFarms/types'
import { useAllTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useWriteContract } from 'hooks/useTransaction'
import { calculateRewardRatePerSecond } from 'pages/KodiakFarmsDeploy/helper'
import { useCallback } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { FarmConfigUpdateInfo, TransactionType } from 'state/transactions/types'
import { Address, encodeFunctionData, formatUnits, parseUnits } from 'viem'

interface UseFarmActionsReturn {
  updateInitialConfig: (newConfig: FarmConfig, originalConfig: FarmConfig) => Promise<any>
  startFarm: (config: FarmConfig) => Promise<any>
  updateLiveConfig: (newConfig: FarmConfig, originalConfig: FarmConfig) => Promise<any>
  validateConfig: (newConfig: FarmConfig, originalConfig: FarmConfig, isLive: boolean) => Promise<ValidationError[]>
  checkRewardTokenBalances: (
    config: FarmConfig,
    originalConfig?: FarmConfig,
    mode?: 'update' | 'start'
  ) => Promise<
    {
      token: string
      symbol: string
      required: number
      available: number
      hasEnough: any
      decimals: number
      source: string
    }[]
  >
  estimateGas: (calls: { target: string; callData: string }[]) => Promise<bigint | null>
  isReady: boolean
}

interface TokenBalanceError {
  type: 'BALANCE_ERROR'
  token: string
  symbol: string
  required: number
  available: number
  decimals: number
}

interface ValidationError {
  type: 'VALIDATION_ERROR'
  field: string
  message: string
}

interface GenericError {
  type: 'GENERIC_ERROR'
  message: string
  field?: string
  // Add any other properties that might be useful
}

export type FarmError = TokenBalanceError | ValidationError | GenericError

export interface FarmErrors {
  error?: FarmError | null
  validationErrors?: ValidationError[]
}

export function useFarmActions(farmAddress: Address): UseFarmActionsReturn {
  const { wagmiPublicClient, account } = useConnection()
  const { writeContractAsync } = useWriteContract()
  const allTokens = useAllTokens()
  const addTransaction = useTransactionAdder()

  // Function to check token allowances
  const checkAllowance = useCallback(
    async (tokenAddress: string, amount: bigint) => {
      if (!wagmiPublicClient || !account) return false
      try {
        const allowance = await wagmiPublicClient.readContract({
          address: tokenAddress as Address,
          abi: ERC20_ABI,
          functionName: 'allowance',
          args: [account, farmAddress],
        })
        return BigInt(allowance as bigint) >= amount
      } catch (error) {
        console.error('Error checking allowance:', error)
        return false
      }
    },
    [wagmiPublicClient, account, farmAddress]
  )

  const checkRewardTokenBalances = useCallback(
    async (config: FarmConfig, originalConfig?: FarmConfig, mode: 'update' | 'start' = 'update') => {
      if (!wagmiPublicClient || !account) throw new Error('Not connected')

      // Check each reward token
      const balanceChecks = await Promise.all(
        config.rewardTokens.map(async (token, index) => {
          // For farm start, always check all tokens
          // For updates, only check tokens with changed rates
          if (mode === 'update' && originalConfig) {
            const originalToken = originalConfig.rewardTokens[index]
            // Skip tokens whose rates haven't changed during updates
            if (
              originalToken &&
              originalToken.tokenAddress === token.tokenAddress &&
              originalToken.rewardRate === token.rewardRate
            ) {
              return {
                token: token.tokenAddress,
                symbol: allTokens[token.tokenAddress]?.symbol || 'Unknown',
                required: 0,
                available: 0,
                hasEnough: true,
                decimals: allTokens[token.tokenAddress]?.decimals ?? 18,
                source: 'unchanged',
              }
            }
          }

          const decimals = allTokens[token.tokenAddress]?.decimals ?? 18

          // Get current contract balance before anything else
          const contractBalance = await wagmiPublicClient.readContract({
            address: token.tokenAddress as Address,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [farmAddress],
          })

          // Calculate our own estimate of what might be needed (for display purposes)
          let clientEstimate = 0n
          if (mode === 'start' || !originalConfig) {
            // For starting farms, estimate the full amount for all tokens
            clientEstimate = BigInt(token.rewardRate) * BigInt(config.rewardsDuration)
          } else if (originalConfig) {
            // For updates, estimate only for increased rates
            const originalToken = originalConfig.rewardTokens.find((t) => t.tokenAddress === token.tokenAddress)
            if (originalToken && BigInt(token.rewardRate) > BigInt(originalToken.rewardRate)) {
              const rateDifference = BigInt(token.rewardRate) - BigInt(originalToken.rewardRate)
              clientEstimate = rateDifference * BigInt(config.rewardsDuration)
            }
          }

          // Call the contract to get the actual funding needed
          let fundingNeeded = 0n
          try {
            const result = await wagmiPublicClient.readContract({
              address: farmAddress,
              abi: KODIAK_FARM_ABI,
              functionName: 'rewardFundingNeeded',
              args: [token.tokenAddress as Address, BigInt(token.rewardRate)],
            })
            fundingNeeded = BigInt(result as bigint)
          } catch (error) {
            console.error('Error calling rewardFundingNeeded:', error)
            // Fall back to our estimate if contract call fails
            fundingNeeded = clientEstimate
          }

          // If no funding is needed according to the contract
          if (fundingNeeded === 0n) {
            // If we have a rate increase but no funding needed, the contract already has enough tokens
            if (clientEstimate !== 0n) {
              return {
                token: token.tokenAddress,
                symbol: allTokens[token.tokenAddress]?.symbol || 'Unknown',
                required: Number(formatUnits(clientEstimate, decimals)),
                available: Number(formatUnits(contractBalance as bigint, decimals)),
                hasEnough: true,
                decimals,
                contractBalance: Number(formatUnits(contractBalance as bigint, decimals)),
                source: 'contract', // Contract has sufficient balance
              }
            } else {
              // Otherwise no change needed
              return {
                token: token.tokenAddress,
                symbol: allTokens[token.tokenAddress]?.symbol || 'Unknown',
                required: 0,
                available: 0,
                hasEnough: true,
                decimals,
                contractBalance: Number(formatUnits(contractBalance as bigint, decimals)),
                source: 'no_change_needed',
              }
            }
          }

          // If we get here, funding is needed from wallet
          const userBalance = await wagmiPublicClient.readContract({
            address: token.tokenAddress as Address,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [account],
          })

          return {
            token: token.tokenAddress,
            symbol: allTokens[token.tokenAddress]?.symbol || 'Unknown',
            required: Number(formatUnits(fundingNeeded, decimals)),
            available: Number(formatUnits(userBalance as bigint, decimals)),
            hasEnough: BigInt(userBalance as bigint) >= fundingNeeded,
            contractBalance: Number(formatUnits(contractBalance as bigint, decimals)),
            decimals,
            source: 'wallet',
          }
        })
      )

      // Filter out tokens that don't need checking
      const relevantBalanceChecks = balanceChecks.filter(
        (check) => !['unchanged', 'no_change_needed', 'no_additional_funds'].includes(check.source)
      )

      const insufficientBalance = relevantBalanceChecks.find((check) => !check.hasEnough)
      if (insufficientBalance) {
        throw {
          type: 'BALANCE_ERROR',
          token: insufficientBalance.token,
          symbol: insufficientBalance.symbol,
          required: insufficientBalance.required,
          available: insufficientBalance.available,
          decimals: insufficientBalance.decimals,
          source: insufficientBalance.source,
        } as TokenBalanceError
      }

      return relevantBalanceChecks
    },
    [wagmiPublicClient, account, farmAddress, allTokens]
  )

  const buildConfigCalls = useCallback(
    (newConfig: FarmConfig, originalConfig: FarmConfig, decimals: number) => {
      if (!farmAddress) return []
      const calls: { callData: string }[] = []

      if (newConfig.rewardsDuration !== originalConfig.rewardsDuration) {
        calls.push({
          callData: encodeFunctionData({
            abi: KODIAK_FARM_ABI,
            functionName: 'setRewardsDuration',
            args: [BigInt(newConfig.rewardsDuration)],
          }),
        })
      }

      if (newConfig.stakingTokenCap !== originalConfig.stakingTokenCap) {
        calls.push({
          callData: encodeFunctionData({
            abi: KODIAK_FARM_ABI,
            functionName: 'setStakingTokenCap',
            args: [parseUnits(newConfig.stakingTokenCap, decimals)],
          }),
        })
      }

      if (
        newConfig.lockTimeMax !== originalConfig.lockTimeMax ||
        newConfig.lockTimeMin !== originalConfig.lockTimeMin
      ) {
        const lockTimeMaxValue = parseInt(newConfig.lockTimeMax) === 0 ? 1 : newConfig.lockTimeMax

        calls.push({
          callData: encodeFunctionData({
            abi: KODIAK_FARM_ABI,
            functionName: 'setLockedStakeTimeForMinAndMaxMultiplier',
            args: [lockTimeMaxValue, newConfig.lockTimeMin],
          }),
        })
      }

      if (newConfig.lockMultiplierMax !== originalConfig.lockMultiplierMax) {
        calls.push({
          callData: encodeFunctionData({
            abi: KODIAK_FARM_ABI,
            functionName: 'setMultipliers',
            args: [parseUnits(newConfig.lockMultiplierMax.toString(), decimals)],
          }),
        })
      }

      newConfig.rewardTokens.forEach((token, index) => {
        const originalToken = originalConfig.rewardTokens[index]

        if (!originalToken) {
          calls.push({
            callData: encodeFunctionData({
              abi: KODIAK_FARM_ABI,
              functionName: 'addNewRewardToken',
              args: [token.tokenAddress, token.rewardManager, token.rewardRate],
            }),
          })
        } else {
          if (token.rewardManager !== originalToken.rewardManager) {
            calls.push({
              callData: encodeFunctionData({
                abi: KODIAK_FARM_ABI,
                functionName: 'changeTokenManager',
                args: [token.tokenAddress, token.rewardManager],
              }),
            })
          }

          if (token.rewardRate !== originalToken.rewardRate) {
            calls.push({
              callData: encodeFunctionData({
                abi: KODIAK_FARM_ABI,
                functionName: 'setRewardRate',
                args: [token.tokenAddress, token.rewardRate, false],
              }),
            })
          }
        }
      })

      return calls
    },
    [farmAddress]
  )

  const validateConfig = useCallback(
    async (newConfig: FarmConfig, originalConfig: FarmConfig, isLive: boolean): Promise<ValidationError[]> => {
      const errors: ValidationError[] = []

      if (isLive && wagmiPublicClient && farmAddress) {
        try {
          const periodFinish = await wagmiPublicClient.readContract({
            address: farmAddress as Address,
            abi: KODIAK_FARM_ABI,
            functionName: 'periodFinish',
          })

          if (newConfig.rewardsDuration !== originalConfig.rewardsDuration && BigInt(periodFinish as bigint) > 0n) {
            errors.push({
              field: 'rewardsDuration',
              type: 'VALIDATION_ERROR',
              message: 'Cannot change rewards duration while current period is active',
            })
          }
        } catch (error) {
          console.error('Error checking periodFinish:', error)
        }
      }

      const rewardsDurationNum = parseInt(newConfig.rewardsDuration)
      if (rewardsDurationNum < 86400) {
        errors.push({
          field: 'rewardsDuration',
          type: 'VALIDATION_ERROR',
          message: 'Rewards duration must be at least 1 day (86400 seconds)',
        })
      }

      const lockTimeMax = parseInt(newConfig.lockTimeMax)
      const lockTimeMin = parseInt(newConfig.lockTimeMin)

      //lockTimeForMaxMultiplier
      if (lockTimeMax < lockTimeMin) {
        errors.push({
          field: 'lockTimeMax',
          type: 'VALIDATION_ERROR',
          message: 'Maximum lock time must be greater than minimum lock time',
        })
      }

      if (lockTimeMin > lockTimeMax) {
        errors.push({
          field: 'lockTimeMin',
          type: 'VALIDATION_ERROR',
          message: 'Minimum lock time must be less than maximum lock time',
        })
      }

      // Could also add validation for stakingTokenCap
      const stakingTokenCapValue = newConfig.stakingTokenCap
      if (stakingTokenCapValue !== '-1' && parseInt(stakingTokenCapValue) <= 0) {
        errors.push({
          type: 'VALIDATION_ERROR',
          field: 'stakingTokenCap',
          message: 'Staking token cap must be -1 (unlimited) or greater than 0',
        })
      }

      return errors
    },
    [wagmiPublicClient, farmAddress]
  )

  const updateInitialConfig = useCallback(
    async (newConfig: FarmConfig, originalConfig: FarmConfig) => {
      if (!account || !farmAddress) throw new Error('Account or farm address not available')

      // Validate config first
      const validationErrors = await validateConfig(newConfig, originalConfig, false)
      if (validationErrors.length > 0) {
        throw validationErrors[0] // Throw the first validation error
      }
      const decimals = allTokens[farmAddress]?.decimals ?? 18
      const calls = buildConfigCalls(newConfig, originalConfig, decimals).map((call) => call.callData)

      try {
        // Use the multicall function with wagmi
        const hash = await writeContractAsync({
          address: farmAddress as Address,
          abi: KODIAK_FARM_ABI,
          functionName: 'multicall',
          args: [calls],
        })

        const transactionInfo: FarmConfigUpdateInfo = {
          type: TransactionType.FARM_CONFIG_UPDATE,
          farmAddress,
          stakingTokenCap: newConfig.stakingTokenCap,
          rewardsDuration: newConfig.rewardsDuration,
          lockTimeMin: newConfig.lockTimeMin,
          lockTimeForMaxMultiplier: newConfig.lockTimeMax,
          maxMultiplier: newConfig.lockMultiplierMax.toString(),
          rewardTokens: newConfig.rewardTokens.map((token) => ({
            ...token,
            rewardRate: token.rewardRate.toString(),
            rewardRateFormatted: token.rewardRateFormatted?.toString(),
          })),
        }

        addTransaction({ hash }, transactionInfo)

        return { hash }
      } catch (error) {
        console.error('Multicall error:', error)
        throw error
      }
    },
    [account, farmAddress, buildConfigCalls, writeContractAsync, addTransaction, allTokens, validateConfig]
  )

  const startFarm = useCallback(async () => {
    if (!account || !farmAddress) throw new Error('Account or farm address not available')

    const hash = await writeContractAsync({
      address: farmAddress,
      abi: KODIAK_FARM_ABI,
      functionName: 'startFarm',
    })

    addTransaction(
      { hash },
      {
        type: TransactionType.FARM_START,
        farmAddress,
      }
    )
    return { hash }
  }, [account, farmAddress, writeContractAsync, addTransaction])

  const updateLiveConfig = useCallback(
    async (newConfig: FarmConfig, originalConfig: FarmConfig) => {
      if (!wagmiPublicClient || !account || !farmAddress)
        throw new Error('Wagmi client, account, or farm address not available')

      const validationErrors = await validateConfig(newConfig, originalConfig, false)
      if (validationErrors.length > 0) {
        throw validationErrors[0]
      }

      // Calculate rewards for tokens with new/increased rates
      const tokenChanges = await Promise.all(
        newConfig.rewardTokens.map(async (token) => {
          const originalToken = originalConfig.rewardTokens.find((t) => t.tokenAddress === token.tokenAddress)
          const decimals = allTokens[token.tokenAddress]?.decimals ?? 18

          const isNew = !originalToken
          const hasIncreasedRate = originalToken && BigInt(token.rewardRate) > BigInt(originalToken.rewardRate)

          if (isNew || hasIncreasedRate) {
            const ratePerSecond = calculateRewardRatePerSecond(
              token.rewardRate,
              parseInt(newConfig.rewardsDuration),
              decimals
            )

            let amountNeeded: bigint

            if (isNew) {
              amountNeeded = ratePerSecond * BigInt(newConfig.rewardsDuration)
            } else {
              // Call on-chain function to get the exact amount needed
              try {
                const result = await wagmiPublicClient.readContract({
                  address: farmAddress,
                  abi: KODIAK_FARM_ABI,
                  functionName: 'rewardFundingNeeded',
                  args: [token.tokenAddress as Address, BigInt(token.rewardRate)],
                })
                amountNeeded = BigInt(result as bigint)
              } catch (error) {
                console.error('Error calling rewardFundingNeeded:', error)

                // Fall back to client-side estimation if contract call fails
                const originalRatePerSecond = calculateRewardRatePerSecond(
                  originalToken.rewardRate,
                  parseInt(newConfig.rewardsDuration),
                  decimals
                )
                const rateDifference = BigInt(ratePerSecond.toString()) - originalRatePerSecond
                amountNeeded = rateDifference * BigInt(newConfig.rewardsDuration)
              }
            }

            return {
              token: token.tokenAddress,
              symbol: allTokens[token.tokenAddress]?.symbol || 'Unknown',
              amountNeeded,
              decimals,
            }
          }
          return null
        })
      )

      // Filter out null values from tokenChanges
      const validTokenChanges = tokenChanges.filter(Boolean)

      // Now check contract balance first, then user balance if needed
      const requiredApprovals = []
      for (const change of validTokenChanges) {
        if (!change) continue // Skip if change is null
        const { token, amountNeeded, symbol, decimals } = change

        // Check farm contract's balance
        const contractBalance = await wagmiPublicClient.readContract({
          address: token as Address,
          abi: ERC20_ABI,
          functionName: 'balanceOf',
          args: [farmAddress as Address],
        })

        // If contract already has enough tokens, skip approval
        if (BigInt(contractBalance as bigint) >= amountNeeded) {
          continue
        }

        // Calculate shortfall & check user balance
        const shortfall = amountNeeded - BigInt(contractBalance as bigint)
        const userBalance = await wagmiPublicClient.readContract({
          address: token as Address,
          abi: ERC20_ABI,
          functionName: 'balanceOf',
          args: [account],
        })

        if (BigInt(userBalance as bigint) < shortfall) {
          throw {
            type: 'BALANCE_ERROR',
            token,
            symbol,
            required: Number(formatUnits(shortfall, decimals)),
            available: Number(formatUnits(userBalance as bigint, decimals)),
            decimals,
            source: 'wallet',
          } as TokenBalanceError
        }

        // Add to approvals if user has enough to cover shortfall
        requiredApprovals.push({ token, amount: shortfall, symbol })
      }

      // Handle approvals
      for (const approval of requiredApprovals) {
        const { token, amount } = approval
        const hasAllowance = await checkAllowance(token, amount)
        if (!hasAllowance) {
          // User needs to approve - this will throw an error to prompt the user
          throw new Error(`Please approve ${approval.symbol} token spending before updating the farm`)
        }
      }

      const decimals = allTokens[farmAddress]?.decimals ?? 18
      const calls = buildConfigCalls(newConfig, originalConfig, decimals)
      if (calls.length === 0) throw new Error('No changes to save')

      const hash = await writeContractAsync({
        address: farmAddress,
        abi: KODIAK_FARM_ABI,
        functionName: 'multicall',
        args: [calls.map((call) => call.callData)],
      })

      addTransaction(
        { hash },
        {
          type: TransactionType.FARM_CONFIG_UPDATE,
          farmAddress,
          stakingTokenCap: newConfig.stakingTokenCap,
          rewardsDuration: newConfig.rewardsDuration,
          lockTimeMin: newConfig.lockTimeMin,
          lockTimeForMaxMultiplier: newConfig.lockTimeMax,
          maxMultiplier: newConfig.lockMultiplierMax.toString(),
          rewardTokens: newConfig.rewardTokens,
        }
      )

      return { hash }
    },
    [
      wagmiPublicClient,
      account,
      validateConfig,
      farmAddress,
      buildConfigCalls,
      checkAllowance,
      writeContractAsync,
      addTransaction,
      allTokens,
    ]
  )

  const estimateGas = useCallback(
    async (calls: { target: string; callData: string }[]) => {
      if (!wagmiPublicClient || !farmAddress) throw new Error('Wagmi client or farm address not available')
      if (calls.length === 0) return null

      try {
        const gas = await wagmiPublicClient.estimateContractGas({
          address: farmAddress,
          abi: KODIAK_FARM_ABI,
          functionName: 'multicall',
          args: [calls.map((c) => c.callData)],
          account,
        })
        return gas
      } catch (error) {
        console.error('Error estimating gas:', error)
        return null
      }
    },
    [wagmiPublicClient, farmAddress, account]
  )

  return {
    updateInitialConfig,
    startFarm,
    updateLiveConfig,
    estimateGas,
    validateConfig,
    checkRewardTokenBalances,
    isReady: !!wagmiPublicClient && !!farmAddress,
  }
}
